/**
 * The Play header's back control (C2, same kinds/order as the app's spec B
 * §B1): one switch over the SAME `selectRailState` signal `Body` and `Rail`
 * already branch on, so the exit path cannot drift from what is actually on
 * screen.
 *
 *   watch                        -> stop watching the game
 *   seeking                      -> cancel the seek
 *   challengeOut                 -> cancel the outgoing challenge
 *   matched / live / evalPending -> a game of mine is up: CONFIRM first (it
 *     keeps running without the player, unlike every other kind here)
 *   tournament                   -> the same exit the Details tab's own
 *     "leave" control uses (`rail/tournament/leaveTournament`) — `Body` swaps
 *     back to the lobby, no navigation
 *   home / result                -> Dashboard
 *
 * `needsConfirm` lets the View (`chrome/PlayHeader`) gate `run` behind
 * `ui/ConfirmDialog`, exactly as `ResignTile.View` gates `resign` — the open
 * flag and the copy are the View's own; this hook owns only the action.
 */
import {useCallback} from 'react';

import {selectRailState} from '@/Store/liveGame/selectors';
import {liveGameStore, useLiveGame} from '@/Store/liveGameStore';

import {leaveTournament} from '../rail/tournament/leaveTournament';
import {navigateTo} from '../rail/navigateRef';

/**
 * The route NAME as a literal, like `overlay/ResultOverlay`'s `REVIEW_ROUTE`:
 * `@/navigators/StackConstants` imports every screen (including this one), so
 * importing it from inside the V2 tree makes a webpack module cycle that left
 * the rail's exports undefined at evaluation time (crash 2026-10-05).
 */
const DASHBOARD_ROUTE = 'Dashboard';

export type BackActionModel = {
  /** true while a game of mine is up — the only kind that asks first */
  needsConfirm: boolean;
  /** the exit itself; the View runs it directly, or on the dialog's Yes */
  run: () => void;
};

export function useBackAction(): BackActionModel {
  const railState = useLiveGame(selectRailState);

  const run = useCallback(() => {
    switch (railState) {
      case 'watch':
        liveGameStore.getState().actions.stopWatchingGame();
        return;
      case 'seeking':
        liveGameStore.getState().actions.cancelSeek();
        return;
      case 'challengeOut':
        liveGameStore.getState().actions.cancelChallenge();
        return;
      case 'tournament':
        leaveTournament();
        return;
      default:
        // 'home' | 'result', and 'matched' | 'live' | 'evalPending' once the
        // confirm below has run this.
        navigateTo(DASHBOARD_ROUTE);
    }
  }, [railState]);

  const needsConfirm =
    railState === 'matched' ||
    railState === 'live' ||
    railState === 'evalPending';

  return {needsConfirm, run};
}

export default useBackAction;
